import {
    products,
    SelectedWeatherItem,
    SelectedWeatherItemType,
    WeatherItemUpdateLate,
    WeatherSettingsChangeSource,
    WeatherStoreProductFrameTimeInfo
} from '@fe/weather';
import { Button, ButtonHandle, Chip } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Loader } from '@progress/kendo-react-indicators';
import { Checkbox } from '@progress/kendo-react-inputs';
import { Popover } from '@progress/kendo-react-tooltip';
import React, { useEffect } from 'react';
import { EventBus } from '../../app/contexts/event-bus';
import { useAppSelector } from '../../app/hooks/store-hooks';
import { WeatherLegendDateTimeDisplay } from '../../common/enums';
import { DateTimeFormatter } from '../../formatters';
import useDebugFeatures from '../../hooks/use-debug-features';
import {
    BugOutline,
    ChevronDownOutline,
    ChevronUpOutline,
    CloseOutline,
    CloudListSolid,
    CogOutline,
    SortableSolid,
    TrashOutline,
    VisibilityOffOutline,
    VisibilityOutline,
    WarningTriangleOutline
} from '../../icons/icons';
import useWeatherService from '../hooks/use-weather-service';
import useSelectedWeather from './hooks/use-selected-weather';
import { useBooleanSetting } from './settings/sections/hooks/useSetting';

interface WeatherTimeSettings {
    style: WeatherLegendDateTimeDisplay;
    displayModelTime: boolean;
}

const colors: Record<WeatherItemUpdateLate, string> = {
    [WeatherItemUpdateLate.no]: '',
    [WeatherItemUpdateLate.yellow]: 'monstat-txt-warning',
    [WeatherItemUpdateLate.red]: 'monstat-txt-critical'
};

const WeatherList = () => {
    const weatherService = useWeatherService();
    const debug = useDebugFeatures();
    const selectedProducts = useSelectedWeather();
    const displayLayers = React.useMemo(
        () => selectedProducts.filter((x) => x.type === SelectedWeatherItemType.weather || debug),
        [selectedProducts, debug]
    );
    const worstLate = React.useMemo(
        () => selectedProducts.reduce((p, n) => Math.max(p, n.updateLate), WeatherItemUpdateLate.no),
        [selectedProducts]
    );
    const [showSettings, setShowSettings] = React.useState(false);
    const autoArrange = useBooleanSetting('other', 'autoArrangeLayers', WeatherSettingsChangeSource.autoArrange);
    const settingsButtonRef = React.useRef<ButtonHandle>(null);
    const [isExpanded, setIsExpanded] = React.useState(false);
    const dateStyle = useAppSelector((x) => x.settings.weatherLegendDateTimeDisplayStyle);
    const showModelRunTime = useAppSelector((x) => x.settings.weatherLegendDisplayModelRunTime);
    const tutorial = useAppSelector((x) => x.global.tutorial);

    const settings = React.useMemo(
        () =>
            ({
                style: dateStyle,
                displayModelTime: showModelRunTime
            }) satisfies WeatherTimeSettings,
        [dateStyle, showModelRunTime]
    );

    const handleToggleVisibility = (product: SelectedWeatherItem) => {
        weatherService!.showHideGdf(product, !product.visible);
    };
    const handleToggleWmsVisibility = (product: SelectedWeatherItem) => {
        weatherService!.showHideWms(product, !product.wms);
    };

    const handleMoveLayerUp = (item: SelectedWeatherItem) => weatherService!.moveProductUp(item);

    const handleMoveLayerDown = (item: SelectedWeatherItem) => weatherService!.moveProductDown(item);

    const handleRemoveLayer = (item: SelectedWeatherItem) => {
        weatherService!.removeProduct(item.id, item.type);
    };

    const lateColor =
        worstLate === WeatherItemUpdateLate.yellow
            ? 'var(--color-semantic-yellow)'
            : worstLate === WeatherItemUpdateLate.red
              ? 'var(--color-semantic-red)'
              : undefined;

    useEffect(() => {
        if (tutorial) {
            setIsExpanded(false);
        }
    }, [tutorial]);

    return (
        <div className="overflow-auto">
            {!isExpanded && (
                <Button
                    id="weather-legend-btn"
                    onClick={() => setIsExpanded(true)}
                    className="pe-auto"
                    svgIcon={CloudListSolid}
                    style={{ backgroundColor: lateColor }}></Button>
            )}

            <div className="max-h-100 d-flex align-items-start flex-column gap-1" id="weather-legend-list">
                <Popover
                    anchor={settingsButtonRef.current?.element}
                    collision={{
                        horizontal: 'fit',
                        vertical: 'fit'
                    }}
                    position="bottom"
                    onClose={() => setShowSettings(false)}
                    show={showSettings}>
                    <div className="d-flex gap-2" style={{ minWidth: 150 }}>
                        <Checkbox label="Auto arrange" {...autoArrange} />
                        <div className="flex-grow-1"></div>
                        <Button svgIcon={CloseOutline} className="button-small" fillMode={'flat'} onClick={() => setShowSettings(false)} />
                    </div>
                </Popover>
                {isExpanded && (
                    <div className="d-flex k-card pe-auto">
                        {/* Title */}
                        <div
                            className="d-flex align-items-center bg-color px-2 py-1"
                            style={{ backgroundColor: lateColor }}
                            onClick={() => {
                                setShowSettings(false);
                                setIsExpanded(!isExpanded);
                            }}>
                            <SvgIcon icon={CloudListSolid} />
                            <div className="px-2">Weather layers ({displayLayers.length})</div>
                            <div className="flex-grow-1"></div>
                            <Button
                                ref={settingsButtonRef}
                                id="weather-list-settings-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowSettings(!showSettings);
                                }}
                                svgIcon={SortableSolid}
                                className="button-small"
                                fillMode={'flat'}
                            />
                            <Button svgIcon={ChevronUpOutline} fillMode={'flat'} className="button-small"></Button>
                        </div>
                        {/* Content */}
                        <div
                            className="px-2 overflow-auto border-top"
                            style={{
                                fontSize: '0.77rem'
                            }}>
                            {displayLayers.map((x, i) => (
                                <div key={i} className="d-flex gap-1 align-items-center" style={{ opacity: x.visible ? 1 : 0.6 }}>
                                    <div style={{ width: 15 }} className="d-flex justify-content-center align-items-center">
                                        {x.loaded && x.success && x.type === SelectedWeatherItemType.weather && (
                                            <Button
                                                className="button-small"
                                                fillMode={'flat'}
                                                svgIcon={x.visible ? VisibilityOutline : VisibilityOffOutline}
                                                onClick={() => handleToggleVisibility(x)}
                                            />
                                        )}
                                        {x.loaded && x.success && x.type === SelectedWeatherItemType.global && <span>G</span>}
                                        {x.loaded && x.success && x.type === SelectedWeatherItemType.static && <span>S</span>}
                                        {!x.loaded && <Loader size="small" />}
                                        {x.loaded && !x.success && (
                                            <SvgIcon icon={WarningTriangleOutline} themeColor="error" size="small" />
                                        )}
                                    </div>
                                    <div className={'flex-grow-1 ' + colors[x.updateLate]}>
                                        {debug && <span className="font-mono">(0x{x.id.toString(16).padStart(4, '0')}) </span>}
                                        {x.loaded && x.success && x.selectedFrame && (
                                            <WeatherProductTime times={x.selectedFrame.times} settings={settings} />
                                        )}
                                        <span> {x.product.name}</span>
                                        <span> [{products[x.id]?.vendor}]</span>
                                        {debug && x.loaded && !x.success && x.error && <span className="text-danger"> [{x.error}]</span>}
                                    </div>

                                    {x.loaded && x.success && (
                                        <>
                                            {debug && (
                                                <Button
                                                    svgIcon={BugOutline}
                                                    className="button-small"
                                                    disabled={!x.gdf}
                                                    fillMode={'flat'}
                                                    style={{
                                                        width: 10
                                                    }}
                                                    onClick={() => EventBus.fire('debug-gdf', { item: x })}></Button>
                                            )}
                                            {import.meta.env.DEV && (
                                                <Chip
                                                    themeColor={x.wms ? 'success' : 'error'}
                                                    size={'small'}
                                                    style={{ height: 18, fontSize: '0.8em' }}
                                                    onClick={() => handleToggleWmsVisibility(x)}>
                                                    WMS
                                                </Chip>
                                            )}
                                            {!autoArrange.value && (
                                                <>
                                                    <Button
                                                        className="button-small"
                                                        style={{
                                                            width: 10
                                                        }}
                                                        fillMode={'flat'}
                                                        svgIcon={ChevronUpOutline}
                                                        onClick={() => handleMoveLayerUp(x)}
                                                    />
                                                    <Button
                                                        className="button-small"
                                                        fillMode={'flat'}
                                                        style={{
                                                            width: 10
                                                        }}
                                                        svgIcon={ChevronDownOutline}
                                                        onClick={() => handleMoveLayerDown(x)}
                                                    />
                                                </>
                                            )}
                                            <Button
                                                svgIcon={CogOutline}
                                                className="button-small"
                                                fillMode={'flat'}
                                                style={{
                                                    width: 10
                                                }}
                                                onClick={() => {
                                                    EventBus.fire('open-weather-settings', { category: x.product.category });
                                                }}></Button>
                                        </>
                                    )}
                                    {x.loaded && x.type === SelectedWeatherItemType.weather && (
                                        <Button
                                            className="button-small"
                                            fillMode={'flat'}
                                            svgIcon={TrashOutline}
                                            onClick={() => handleRemoveLayer(x)}
                                        />
                                    )}
                                </div>
                            ))}

                            {displayLayers.length === 0 && <div className="secondary-text p-2">No weather products selected.</div>}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

function WeatherProductTime({ settings, times }: { times: WeatherStoreProductFrameTimeInfo; settings: WeatherTimeSettings }) {
    let validFrom = '';

    if (settings.style === WeatherLegendDateTimeDisplay.timeOnly) {
        validFrom = DateTimeFormatter.formatTime(times.validFrom, 'hmnd');
    } else if (settings.style === WeatherLegendDateTimeDisplay.timeDate) {
        validFrom = DateTimeFormatter.formatDateTime(times.validFrom);
    } else if (settings.style === WeatherLegendDateTimeDisplay.timeDay) {
        validFrom = DateTimeFormatter.formatTime(times.validFrom, 'hmd');
    }

    let modelTime = '';
    if (settings.displayModelTime) {
        modelTime = DateTimeFormatter.formatTime(times.createdAt, 'hmnd');
    }

    return (
        <span>
            {validFrom}
            {validFrom && modelTime && '/'}
            {modelTime}
        </span>
    );
}

export default WeatherList;
